import { defineStore } from "pinia";
import useAwardStore from "@/stores/award";
import { honoClient } from "@/boa-api-client";
import type { Category, Group, MasterCategory } from "best-of-awards-api";
import type { CreateCategory } from "best-of-awards-api/src/lib/categories/create-category";
import type { UpdateCategory } from "best-of-awards-api/src/lib/categories/update-category";

type State = {
  categories: Category[];
  groups: Group[];
  nominationStageCategories: Category[];
  masterCategories: MasterCategory[];
  loaded: boolean;
};

export default defineStore("categories", {
  state: (): State => ({
    categories: [],
    groups: [],
    nominationStageCategories: [],
    masterCategories: [],
    loaded: false,
  }),
  getters: {
    awardYearCategories(state): Category[] {
      const { awardYear } = useAwardStore();
      if (awardYear) {
        return state.categories
          .filter((c) => {
            return c.awardYears && c.awardYears.includes(awardYear);
          })
          .sort((a, b) => {
            return a.name.localeCompare(b.name);
          });
      }
      return [];
    },
    unfinalizedCategories(state): Category[] {
      const { awardYear } = useAwardStore();
      if (!awardYear) {
        return [];
      }
      return state.categories.filter((c) => {
        return (
          c.awardYears.includes(awardYear) &&
          !c.finalizedYears?.includes(awardYear)
        );
      });
    },
    unselectedCategories(): Category[] {
      const { awardYear } = useAwardStore();
      return this.awardYearCategories.filter(
        (c) => !c.preselectedYears || !c.preselectedYears.includes(awardYear)
      );
    },
    queuedCategoriesExisting(): Category[] {
      return this.awardYearCategories.filter((c) => c.existingQueued);
    },
    queuedCategoriesNew(): Category[] {
      return this.awardYearCategories.filter((c) => c.newQueued);
    },
    finalistsCategories(): Category[] {
      const awardStore = useAwardStore();
      return this.awardYearCategories.filter((c) =>
        c.finalistsYears?.includes(awardStore.awardYear)
      );
    },
  },
  actions: {
    async getCategories() {
      try {
        const response = await honoClient.categories.$get();
        if (!response.ok) {
          throw new Error(response.statusText);
        }
        const categories = await response.json();
        this.groups = categories.groups;
        const allCategories = categories.allCategories.sort(
          (a: Category, b: Category) => {
            return a.name.localeCompare(b.name);
          }
        );
        this.categories = allCategories;
        this.nominationStageCategories = categories.nominationStageCategories;
        this.masterCategories = categories.masterCategories;
        return {
          categories: allCategories,
          groups: categories.groups,
          masterCategories: categories.masterCategories,
          nominationStageCategories: categories.nominationStageCategories,
        };
      } catch (error) {
        console.error(error);
      }
    },
    async updateCategory(category: UpdateCategory) {
      const response = await honoClient.categories[":id"].$post({
        param: { id: category.id },
        json: category,
      });
      if (!response.ok) {
        throw new Error(response.statusText);
      }
      return response.json();
    },
    async createCategory(category: CreateCategory) {
      const response = await honoClient.categories.$put({
        json: category,
      });
      if (!response.ok) {
        throw new Error(response.statusText);
      }
      return response.json();
    },
    finalizeCategory(categoryId: string) {
      const { awardYear } = useAwardStore();
      const category = this.categories.find((c) => c.id === categoryId);
      if (!category) {
        return;
      }
      if (!category.finalizedYears) {
        category.finalizedYears = [awardYear];
      } else if (!category.finalizedYears.includes(awardYear)) {
        category.finalizedYears.push(awardYear);
      }
    },
    finalizeCategoryFinalists(categoryId: string) {
      const { awardYear } = useAwardStore();
      const category = this.categories.find((c) => c.id === categoryId);
      if (!category) {
        return;
      }
      if (!category.finalistsYears) {
        category.finalistsYears = [awardYear];
      } else if (!category.finalistsYears.includes(awardYear)) {
        category.finalistsYears.push(awardYear);
      }
    },
    finalizeCategoryWinners(categoryId: string) {
      const { awardYear } = useAwardStore();
      const category = this.categories.find((c) => c.id === categoryId);
      if (!category) {
        return;
      }
      if (!category.winnersYears) {
        category.winnersYears = [awardYear];
      } else if (!category.winnersYears.includes(awardYear)) {
        category.winnersYears.push(awardYear);
      }
    },
    finalizeMasterCategoryWinners(masterCategory: MasterCategory) {
      const { awardYear } = useAwardStore();
      const category = this.masterCategories.find(
        (c) => c.name === masterCategory.name
      );
      if (!category) {
        return;
      }
      if (!category.finalizedYears) {
        category.finalizedYears = [awardYear];
      } else if (!category.finalizedYears.includes(awardYear)) {
        category.finalizedYears.push(awardYear);
      }
    },
    async preselectCategory(category: Category & { preselected: boolean }) {
      try {
        const { awardYear } = useAwardStore();
        const response = await honoClient.categories[":id"].preselect.$post({
          param: { id: category.id },
          json: category,
        });
        if (!response.ok) {
          throw new Error(response.statusText);
        }
        if (!category.preselectedYears) {
          category.preselectedYears = [awardYear];
        } else if (!category.preselectedYears.includes(awardYear)) {
          category.preselectedYears.push(awardYear);
        }
      } catch (error) {
        console.error(error);
      }
    },
    async deleteCategory(category: Category) {
      try {
        const response = await honoClient.categories[":name"].$delete({
          param: { name: category.name },
        });
        if (!response.ok) {
          throw new Error(response.statusText);
        }
        this.categories = this.categories.filter(
          (c) => c.name !== category.name
        );
        this.nominationStageCategories = this.categories.filter(
          (c) => c.name !== category.name
        );
        return true;
      } catch (error) {
        console.error(error);
      }
    },
    clear() {
      this.$reset();
    },
  },
});
